import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Search, Edit2, Trash2, Check, X } from 'lucide-react';
import { Coupon } from '@/types/coupon';
import { couponApi } from '@/services/couponApi';
import toast from 'react-hot-toast';

export const CouponsList: React.FC = () => {
  const navigate = useNavigate();
  const [coupons, setCoupons] = useState<Coupon[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    loadCoupons();
  }, []);

  const loadCoupons = async () => {
    try {
      setLoading(true);
      const data = await couponApi.getAllCoupons();
      setCoupons(data);
    } catch (error: any) {
      console.error('Kuponlar yüklenirken hata:', error);
      toast.error(error.message || 'Kuponlar yüklenemedi');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: string, code: string) => {
    if (window.confirm(`"${code}" kuponunu silmek istediğinize emin misiniz?`)) {
      try {
        await couponApi.deleteCoupon(id);
        toast.success('✅ Kupon silindi!');
        loadCoupons();
      } catch (error: any) {
        console.error('Kupon silinirken hata:', error);
        toast.error(error.message || 'Kupon silinemedi');
      }
    }
  };

  const formatDate = (date: Date) => {
    if (!date) return '-';
    return new Date(date).toLocaleDateString('tr-TR');
  };

  const handleToggleActive = async (coupon: Coupon) => {
    try {
      await couponApi.updateCoupon(coupon.id, {
        isActive: !coupon.isActive,
      });
      toast.success(coupon.isActive ? '✅ Kupon deaktif edildi' : '✅ Kupon aktif edildi');
      loadCoupons();
    } catch (error: any) {
      toast.error(error.message || 'Durum güncellenemedi');
    }
  };

  const filteredCoupons = coupons.filter((coupon) => {
    return (
      coupon.code.toLowerCase().includes(searchTerm.toLowerCase()) ||
      coupon.description?.toLowerCase().includes(searchTerm.toLowerCase())
    );
  });

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100">🎟️ Kuponlar</h1>
          <p className="text-gray-600 dark:text-gray-400 mt-1">
            Toplam {filteredCoupons.length} kupon
          </p>
        </div>
        <button
          onClick={() => navigate('/admin/coupons/new')}
          className="inline-flex items-center gap-2 px-4 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition"
        >
          <Plus size={20} />
          Yeni Kupon Ekle
        </button>
      </div>

      {/* Search */}
      <div className="bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-800 p-4">
        <div className="relative">
          <Search className="absolute left-3 top-3 text-gray-400" size={18} />
          <input
            type="text"
            placeholder="Kupon kodu veya açıklama ara..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2.5 rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>
      </div>

      {/* Loading State */}
      {loading && (
        <div className="flex justify-center py-12">
          <div className="animate-spin">
            <div className="w-12 h-12 border-4 border-blue-200 border-t-blue-600 rounded-full"></div>
          </div>
        </div>
      )}

      {/* Table */}
      {!loading && (
        <div className="bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-800 overflow-hidden">
          {filteredCoupons.length === 0 ? (
            <div className="p-8 text-center">
              <p className="text-gray-500 dark:text-gray-400">Kupon bulunamadı</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
                  <tr>
                    <th className="px-6 py-3 text-left text-sm font-semibold text-gray-900 dark:text-gray-100">Kod</th>
                    <th className="px-6 py-3 text-left text-sm font-semibold text-gray-900 dark:text-gray-100">İndirim</th>
                    <th className="px-6 py-3 text-left text-sm font-semibold text-gray-900 dark:text-gray-100">Min. Sipariş</th>
                    <th className="px-6 py-3 text-left text-sm font-semibold text-gray-900 dark:text-gray-100">Kullanım</th>
                    <th className="px-6 py-3 text-left text-sm font-semibold text-gray-900 dark:text-gray-100">Son Tarih</th>
                    <th className="px-6 py-3 text-left text-sm font-semibold text-gray-900 dark:text-gray-100">Durum</th>
                    <th className="px-6 py-3 text-left text-sm font-semibold text-gray-900 dark:text-gray-100">İşlemler</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredCoupons.map((coupon) => (
                    <tr key={coupon.id} className="border-b border-gray-200 dark:border-gray-800 hover:bg-gray-50 dark:hover:bg-gray-800/50 transition">
                      <td className="px-6 py-4 text-sm font-medium text-gray-900 dark:text-gray-100">{coupon.code}</td>
                      <td className="px-6 py-4 text-sm text-gray-600 dark:text-gray-400">
                        {coupon.type === 'percentage'
                          ? `${coupon.discountValue}%`
                          : `₺${coupon.discountValue}`}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-600 dark:text-gray-400">
                        ₺{coupon.minOrderAmount}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-600 dark:text-gray-400">
                        {coupon.usedCount}/{coupon.maxUsesTotal || '∞'}
                        <span className="text-xs text-gray-400 ml-1">(kişi başı: {coupon.maxUsesPerUser})</span>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-600 dark:text-gray-400">
                        {formatDate(coupon.endDate)}
                      </td>
                      <td className="px-6 py-4 text-sm">
                        <button
                          onClick={() => handleToggleActive(coupon)}
                          className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-sm font-medium transition ${
                            coupon.isActive
                              ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400'
                              : 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400'
                          }`}
                        >
                          {coupon.isActive ? (
                            <>
                              <Check size={14} /> Aktif
                            </>
                          ) : (
                            <>
                              <X size={14} /> Deaktif
                            </>
                          )}
                        </button>
                      </td>
                      <td className="px-6 py-4 text-sm space-x-2">
                        <button
                          onClick={() => navigate(`/admin/coupons/${coupon.id}`)}
                          className="inline-flex items-center gap-1 px-3 py-1.5 text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg transition"
                        >
                          <Edit2 size={16} />
                        </button>
                        <button
                          onClick={() => handleDelete(coupon.id, coupon.code)}
                          className="inline-flex items-center gap-1 px-3 py-1.5 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition"
                        >
                          <Trash2 size={16} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default CouponsList;
